import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTransactions, getCurrentMember, isAuthenticated, Transaction, Member } from '../services/api';
import { ArrowLeft, TrendingUp, TrendingDown, ShoppingBag, Gift, Ticket, Clock, Trophy, Medal, Award } from 'lucide-react';

const TransactionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [member, setMember] = useState<Member | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    try {
      const [transactionsData, memberData] = await Promise.all([
        getTransactions(),
        getCurrentMember()
      ]);
      setTransactions(transactionsData.transactions);
      setMember(memberData);
    } catch (err) {
      console.error('Failed to load transactions:', err);
    } finally {
      setLoading(false);
    }
  };

  const getTierIcon = (tier: string) => {
    switch (tier) {
      case 'Gold': return Trophy;
      case 'Silver': return Medal;
      default: return Award;
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'Gold': return 'text-yellow-500';
      case 'Silver': return 'text-gray-400';
      default: return 'text-amber-700';
    }
  };

  const getChannelIcon = (channel: string) => {
    const lowerChannel = channel?.toLowerCase() || '';
    if (lowerChannel.includes('shopify')) return ShoppingBag;
    if (lowerChannel.includes('redemption')) return Ticket;
    if (lowerChannel.includes('auction')) return Gift;
    return Clock;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-buggy-blue mx-auto mb-4"></div>
          <p className="text-buggy-gray">Loading history...</p>
        </div>
      </div>
    );
  }

  const TierIcon = member ? getTierIcon(member.currentTier) : Award;
  const tierColor = member ? getTierColor(member.currentTier) : 'text-amber-700';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Sticky Header */}
      <div className="sticky top-0 z-40">
        <div className="bg-buggy-blue text-white p-6">
          <div className="max-w-md mx-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate('/profile')}
                  className="p-1 -ml-1 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <ArrowLeft size={24} strokeWidth={2} />
                </button>
                <div>
                  <h1 className="text-2xl font-bold">Points History</h1>
                  <p className="text-sm opacity-90">Your earnings and redemptions</p>
                </div>
              </div>
              {member && (
                <div className="flex flex-col items-end">
                  <div className="inline-flex items-center gap-1.5 bg-white rounded-full px-3 py-1.5 mb-2 shadow-sm">
                    <TierIcon size={16} strokeWidth={2.5} className={tierColor} />
                    <span className="text-xs font-semibold text-buggy-dark">{member.currentTier} Tier</span>
                  </div>
                  <p className="text-xs opacity-75">Your Points</p>
                  <p className="text-2xl font-bold">{member.currentPoints}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto pb-20">
        <div className="max-w-md mx-auto p-4">
          {transactions.length === 0 ? (
            /* Empty State */
            <div className="bg-white rounded-xl shadow-md p-8 text-center">
              <div className="w-20 h-20 bg-buggy-blue/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock size={40} strokeWidth={2} className="text-buggy-blue" />
              </div>
              <h2 className="text-xl font-bold text-buggy-dark mb-2">No History Yet</h2>
              <p className="text-buggy-gray mb-6">
                Your points history will appear here as you earn and redeem rewards.
              </p>
              <button
                onClick={() => navigate('/rewards')}
                className="bg-buggy-blue text-white font-semibold py-3 px-6 rounded-lg hover:bg-buggy-blue-light transition-colors"
              >
                Browse Rewards
              </button>
            </div>
          ) : (
            /* Transactions List */
            <div className="space-y-3">
              {transactions.map((transaction) => {
                const isEarn = transaction.type === 'earn';
                const ChannelIcon = getChannelIcon(transaction.channel);

                return (
                  <div
                    key={`${transaction.type}-${transaction.id}`}
                    className="bg-white rounded-xl shadow-md p-4"
                  >
                    <div className="flex items-center gap-4">
                      {/* Icon */}
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        isEarn ? 'bg-green-100' : 'bg-red-100'
                      }`}>
                        {isEarn ? (
                          <TrendingUp size={24} strokeWidth={2} className="text-green-600" />
                        ) : (
                          <TrendingDown size={24} strokeWidth={2} className="text-red-600" />
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-buggy-dark truncate">
                              {transaction.description}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-buggy-gray">
                                {formatDate(transaction.date)} at {formatTime(transaction.date)}
                              </span>
                            </div>
                          </div>

                          {/* Points */}
                          <div className="text-right flex-shrink-0">
                            <p className={`text-lg font-bold ${
                              isEarn ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {isEarn ? '+' : ''}{transaction.points}
                            </p>
                            <p className="text-xs text-buggy-gray">pts</p>
                          </div>
                        </div>

                        {/* Channel Badge */}
                        <div className="mt-2 flex items-center gap-2">
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-gray-100 text-buggy-gray rounded-full">
                            <ChannelIcon size={12} strokeWidth={2} />
                            {transaction.channel}
                          </span>
                          {transaction.orderTotal && (
                            <span className="text-xs text-buggy-gray">
                              ${transaction.orderTotal.toFixed(2)} order
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionsPage;
